import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalDialog,
  ModalClose,
  Typography,
  FormControl,
  FormLabel,
  Select,
  Option,
  Button,
  Stack,
  Box,
  Table,
  Chip,
  Alert,
  Divider,
  LinearProgress,
} from '@mui/joy';
import {
  productMappingsApi,
  distributorsApi,
  type MaterialSpecification,
  type Distributor,
  type DistributorSuggestionResponse,
  type MappedMaterialResult,
} from '../../lib/distributorsApi';
import { useNotification } from '../../contexts/NotificationContext';

interface Props {
  open: boolean;
  onClose: () => void;
  spec: MaterialSpecification | null;
}

const formatCurrency = (amount: number | null | undefined) => {
  if (amount == null) return '-';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
};

export default function MaterialMapModal({ open, onClose, spec }: Props) {
  const { showError } = useNotification();
  const [distributors, setDistributors] = useState<Distributor[]>([]);
  const [selectedDistributor, setSelectedDistributor] = useState<string>('');
  const [suggestion, setSuggestion] = useState<DistributorSuggestionResponse | null>(null);
  const [mapped, setMapped] = useState<MappedMaterialResult[] | null>(null);
  const [loadingSuggest, setLoadingSuggest] = useState(false);
  const [loadingMap, setLoadingMap] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && spec) {
      setSuggestion(null);
      setMapped(null);
      setSelectedDistributor('');
      setError(null);
      loadDistributors();
      loadSuggestion();
    }
  }, [open, spec]);

  const loadDistributors = async () => {
    try {
      const response = await distributorsApi.list({ is_active: true });
      setDistributors(response?.results || []);
    } catch {
      // non-fatal — manual distributor select just stays empty
    }
  };

  const loadSuggestion = async () => {
    if (!spec) return;
    setLoadingSuggest(true);
    setError(null);
    try {
      const result = await productMappingsApi.suggestDistributor([spec.id]);
      setSuggestion(result);
      if (result.recommended) {
        setSelectedDistributor(String(result.recommended.distributor_id));
      }
    } catch (err: any) {
      const detail = err.response?.data?.detail || 'Failed to load distributor suggestions';
      setError(detail);
    } finally {
      setLoadingSuggest(false);
    }
  };

  const handleMap = async () => {
    if (!spec || !selectedDistributor) {
      setError('Please select a distributor');
      return;
    }
    setLoadingMap(true);
    setError(null);
    setMapped(null);
    try {
      const results = await productMappingsApi.mapMaterials(
        [{ material_spec_id: spec.id, quantity: 1 }],
        parseInt(selectedDistributor, 10),
        true,
      );
      setMapped(results);
    } catch (err: any) {
      const detail = err.response?.data?.detail || 'Failed to map materials to products';
      setError(detail);
      showError(detail);
    } finally {
      setLoadingMap(false);
    }
  };

  const confidenceColor = (level?: string): 'success' | 'primary' | 'warning' | 'neutral' => {
    switch (level) {
      case 'High':
        return 'success';
      case 'Medium':
        return 'primary';
      case 'Low':
        return 'warning';
      default:
        return 'neutral';
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog sx={{ width: '95%', maxWidth: 700 }}>
        <ModalClose />
        <Typography level="h3" sx={{ mb: 0.5 }}>
          Map to Products
        </Typography>
        {spec && (
          <Typography level="body-sm" color="neutral" sx={{ mb: 2 }}>
            {spec.category_name ? `${spec.category_name} — ` : ''}
            {spec.name}
          </Typography>
        )}

        <Stack spacing={2}>
          {error && <Alert color="danger">{error}</Alert>}

          {/* Distributor suggestion */}
          <Box>
            <Typography level="title-sm" sx={{ mb: 1 }}>
              Recommended Distributor
            </Typography>
            {loadingSuggest && <LinearProgress />}
            {!loadingSuggest && suggestion?.recommended && (
              <Alert color="success" variant="soft">
                <Box>
                  <Typography level="body-sm" fontWeight="lg">
                    {suggestion.recommended.distributor_name}
                  </Typography>
                  <Typography level="body-xs" color="neutral">
                    {suggestion.recommended.coverage_percent.toFixed(0)}% coverage ·{' '}
                    {suggestion.recommended.materials_available}/
                    {suggestion.recommended.materials_requested} available ·{' '}
                    avg {formatCurrency(suggestion.recommended.average_price)}
                  </Typography>
                </Box>
              </Alert>
            )}
            {!loadingSuggest && suggestion && !suggestion.recommended && (
              <Alert color="neutral" variant="soft">
                No distributor currently has a mapping for this specification.
              </Alert>
            )}
          </Box>

          <Divider />

          {/* Distributor selection + map action */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-end">
            <FormControl sx={{ flex: 1 }}>
              <FormLabel>Distributor</FormLabel>
              <Select
                value={selectedDistributor}
                onChange={(_, value) => setSelectedDistributor((value as string) ?? '')}
                placeholder="Select a distributor"
              >
                {distributors.map((d) => (
                  <Option key={d.id} value={String(d.id)}>
                    {d.name}
                  </Option>
                ))}
              </Select>
            </FormControl>
            <Button
              color="primary"
              onClick={handleMap}
              loading={loadingMap}
              disabled={!selectedDistributor}
            >
              Map Products
            </Button>
          </Stack>

          {/* Mapping results */}
          {mapped && (
            <Box>
              <Typography level="title-sm" sx={{ mb: 1 }}>
                Mapping Results
              </Typography>
              {mapped.length === 0 && (
                <Alert color="neutral">No results returned.</Alert>
              )}
              {mapped.map((result, idx) => (
                <Box key={idx} sx={{ mb: 2 }}>
                  {result.error && <Alert color="danger">{result.error}</Alert>}

                  {result.product_name && !result.requires_manual_selection && (
                    <Alert color="success" variant="soft">
                      <Box sx={{ width: '100%' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography level="body-sm" fontWeight="lg">
                            {result.product_name}
                          </Typography>
                          {result.confidence_level && (
                            <Chip size="sm" color={confidenceColor(result.confidence_level)}>
                              {result.confidence_level}
                            </Chip>
                          )}
                        </Box>
                        <Typography level="body-xs" color="neutral">
                          {result.product_id ? `SKU ${result.product_id} · ` : ''}
                          {formatCurrency(result.unit_price)}
                          {result.quantity != null ? ` · qty ${result.quantity}` : ''}
                        </Typography>
                      </Box>
                    </Alert>
                  )}

                  {result.requires_manual_selection && (result.suggestions?.length ?? 0) > 0 && (
                    <>
                      <Alert color="warning" variant="soft" sx={{ mb: 1 }}>
                        {result.message || 'No exact match — select from suggestions below.'}
                      </Alert>
                      <Box sx={{ overflowX: 'auto' }}>
                        <Table size="sm">
                          <thead>
                            <tr>
                              <th>Product</th>
                              <th>SKU</th>
                              <th>Price</th>
                              <th>Match</th>
                            </tr>
                          </thead>
                          <tbody>
                            {result.suggestions!.map((s) => (
                              <tr key={s.price_list_item_id}>
                                <td>{s.product_name}</td>
                                <td>{s.product_id || '-'}</td>
                                <td>{formatCurrency(s.unit_price)}</td>
                                <td>{(s.similarity_score * 100).toFixed(0)}%</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </Box>
                    </>
                  )}

                  {result.requires_manual_selection &&
                    !(result.suggestions?.length ?? 0) &&
                    !result.error && (
                      <Alert color="neutral">
                        {result.message || 'No matching products found for this distributor.'}
                      </Alert>
                    )}
                </Box>
              ))}
            </Box>
          )}
        </Stack>

        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 3 }}>
          <Button variant="outlined" onClick={onClose}>
            Close
          </Button>
        </Stack>
      </ModalDialog>
    </Modal>
  );
}
